import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import MainPage from './MainPage';
import IncomingCallToast from './IncomingCallToast';
import VideoCallModal from './VideoCallModal';
import { io } from 'socket.io-client';

const Dashboard = () => {
  const role = localStorage.getItem('userRole') || 'staff';
  const name = localStorage.getItem('userName') || 'User';
  // If patient, use their unique ID. If staff, join 'staff' for simplicity, or their specific userId.
  const userId = role === 'patient' ? localStorage.getItem('patientId') : localStorage.getItem('userId') || 'staff'; 

  const [activeSection, setActiveSection] = useState(role === 'patient' ? 'patient-portal' : 'patient-admin');
  const [incomingCall, setIncomingCall] = useState(null); // { id, callerName }
  const [activeCall, setActiveCall] = useState(null);    // Current call session
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const newSocket = io(`${window.location.protocol}//${window.location.hostname}`);
    setSocket(newSocket);

    // Join room for this user to receive signaling
    if (userId) {
       newSocket.emit('join-room', userId);
    }

    // Listen for incoming calls triggered from other parts of the app
    newSocket.on('incoming-call', ({ fromUserName, appointmentId, patientId, doctorId }) => {
       setIncomingCall({ 
          id: appointmentId, 
          callerName: fromUserName,
          patientId: patientId,
          doctorId: doctorId
       });
    });

    newSocket.on('call-rejected', () => {
       setActiveCall(null);
    });

    return () => newSocket.disconnect();
  }, [userId]);

  // Simulation trigger (for dev testing)
  const simulateCall = () => {
     setIncomingCall({ id: Date.now(), callerName: role === 'patient' ? 'Dr. Sarah Wilson' : 'Patient Guest (OPD)' });
  };

  const menuItems = role === 'patient' ? {
    'patient-portal': {
      title: 'Patient Portal',
      items: {
        'my-appointments': {
          title: 'Consultations',
          subItems: ['Book', 'Calendar']
        },
        'health-records': {
          title: 'Records',
          subItems: ['Medical history', 'Lab results', 'Prescriptions']
        }
      }
    }
  } : {
    'patient-admin': {
      title: 'Patient Administration',
      items: {
        'registration-checkin': {
          title: 'Registration & Check-in',
          subItems: ['Registration', 'Check-in', 'Transfer', 'Awaiting transfer']
        },
        'appointments': {
          title: 'Appointments',
          subItems: ['Patient appointment overview', 'Book doctor appointment', 'Telemedicine appointments', 'Location calendars']
        },
        'patient-locations': {
          title: 'Patient Locations',
          subItems: ['OPD patients location', 'ETU patients location', 'IPD patients location', 'Mortuary patients location']
        },
        'queues': {
          title: 'Queues',
          subItems: ['Walk-in queue', 'Virtual queue', 'All current check-in']
        }
      }
    },
    'inpatient': {
      title: 'Inpatient',
      items: {
        'inpatient-admin': {
          title: 'Inpatient Administration',
          subItems: ['Awaiting admission', 'Awaiting check-out']
        },
        'nurses-station': {
          title: 'Nurses Station',
          subItems: ['Ward management', 'Bed charges tracking']
        }
      }
    },
    'ehr': {
      title: 'EHR',
      items: {
        'menu': {
          title: 'Menu',
          subItems: ['Select patient']
        }
      }
    },
    'billing-insurance': {
      title: 'Billing & Insurance',
      items: {
        'invoicing-orders': {
          title: 'Invoicing & Orders',
          subItems: ['Orders', 'Rapid ordering', 'Interim invoices', 'Patient invoice', 'Receipts', 'Point of sales', 'Walk-in visit', 'Telemedicine visit', 'Proforma invoice', 'Invoice balances', 'Future pay agreements and approval', 'Price adjustment', 'Price adjustment approval', 'Refunds', 'Refund deposit', 'Refund patient invoice', 'Refund interim invoice', 'Refund POS Invoice', 'Refund notes and approval']
        },
        'insurance': {
          title: 'Insurance',
          subItems: ['Scheme invoice', 'Insurance Companies', 'Insurance schemes', 'Insurance setup', 'Instant validation', 'Visits credit limits', 'Claim management', 'Debtor Allocation', 'Debtor allocation notes approval', 'Claim submission', 'Claim submission notes approval', 'NHIS claim file records', 'Shifts']
        }
      }
    },
    'accounting': {
      title: 'Accounting',
      items: {
        'accounts-receivable': {
          title: 'Accounts Receivable',
          subItems: ['Debtors', 'Patient', 'Employees', 'Incoming payments', 'Incoming payment receipts', 'Debtor Credit Note', 'Debtor Credit Note Approval', 'Debtor Debit Note']
        },
        'accounts-payable': {
          title: 'Accounts Payable',
          subItems: ['Creditor', 'Taxes', 'Revenue sharing parties', 'Revenue share liabilities', 'Revenue share invoice', 'Service invoice', 'Service invoice approval', 'Purchase invoice', 'Purchase invoice approval', 'Tax invoice', 'Payment vouchers', 'Payment vouchers approval', 'Physical products return', 'Physical products return approval', 'Creditor Credit note', 'Creditor Allocation', 'Creditor allocation notes approval']
        },
        'banking': {
          title: 'Banking',
          subItems: ['Bank accounts', 'Collections', 'Banking notes and approval', 'Bank Statements', 'Bank reconciliation', 'Bank reconciliation items', 'Bank reconciliation approval', 'General ledger accounting', 'Ledger balances', 'Manual journals', 'Transaction notes', 'Payment notes', 'Incoming payments notes', 'Period-end-closing', 'Assets', 'Fixed asset monitoring', 'Fixed asset registration note', 'Purchased assets registration', 'Fixed asset registry - Summary', 'Fixed asset registry - Detail', 'Fixed Assets issue note', 'Fixed assets acknowledgment', 'Scan fixed asset', 'Petty Management', 'Advance and Petty cash', 'Float Management', 'Float Replenishment', 'Petty cash documents']
        }
      }
    },
    'inventory-products': {
      title: 'Inventory & Products',
      items: {
        'control-centre': {
          title: 'Control Centre',
          subItems: ['Stock monitoring', 'Stock adjustment', 'Discrepancy notes and approval']
        },
        'procurement': {
          title: 'Procurement',
          subItems: ['Draft purchase orders', 'Submit purchase orders', 'Approve purchase order', 'Draft service purchase orders', 'Service purchase orders']
        },
        'store-management': {
          title: 'Store Management',
          subItems: ['Submitted requisitions', 'Goods received notes', 'Issue notes', 'Consumables usage', 'Sub-store management', 'Requisitions', 'Acknowledgement']
        },
        'product-services': {
          title: 'Product and Services',
          subItems: ['Create new product', 'Product packages', 'Price lists']
        }
      }
    },
    'laboratory': {
      title: 'Laboratory',
      items: {
        'orders': {
          title: 'Orders',
          subItems: ['New laboratory orders', 'Laboratory orders', 'Drafts', 'New laboratory drafts', 'Laboratory drafts']
        },
        'samples': {
          title: 'Samples',
          subItems: ['Overview', 'Collect samples', 'Approve samples', 'Accepted samples', 'Rejected samples']
        },
        'tests': {
          title: 'Tests',
          subItems: ['Overview', 'Registered tests', 'Record tests results', 'Approve submitted tests', 'Reports for approved tests', 'All tests']
        },
        'external-processes': {
          title: 'External Processes',
          subItems: ['Awaiting sample & test ordering', 'Awaiting test ordering', 'Awaiting external results', 'Laboratory Service orders']
        }
      }
    },
    'pharmacy': {
      title: 'Pharmacy',
      items: {
        'orders': {
          title: 'Orders',
          subItems: ['New pharmacy orders', 'Pharmacy orders', 'Drafts', 'New pharmacy drafts', 'Pharmacy drafts']
        },
        'returns-inventory': {
          title: 'Returns & Inventory',
          subItems: ['Pharmacy return', 'Pharmacy return notes and approval', 'Drug short expiry']
        }
      }
    },
    'imaging': {
      title: 'Imaging',
      items: {
        'orders': {
          title: 'Orders',
          subItems: ['New imaging orders', 'Imaging orders', 'Drafts', 'New imaging drafts', 'Imaging drafts']
        }
      }
    },
    'analytics': {
      title: 'Analytics',
      items: {
        'reports': {
          title: 'Reports',
          subItems: ['Real-time', 'Operational reports', 'Business intelligence', 'Medical reports', 'Country reports']
        }
      }
    },
    'configuration': {
      title: 'Configuration',
      items: {
        'home': {
          title: 'Home',
          subItems: ['Configuration home']
        },
        'general-setup': {
          title: 'General Setup',
          subItems: ['General setup overview', 'Clinic setup', 'Location setup', 'Practitioner setup']
        },
        'patient-administration': {
          title: 'Patient Administration',
          subItems: ['Appointment type setup', 'Registration and check-in/out setup', 'Patient setup']
        },
        'modules': {
          title: 'Modules',
          subItems: ['Patient administration overview', 'EHR configuration overview', 'Billing & insurance overview', 'Accounting overview', 'Inventory & products overview', 'Laboratory overview', 'Pharmacy overview', 'Imaging overview', 'Reporting & Analytics overview']
        },
        'clinical-setup': {
          title: 'Clinical Setup',
          subItems: ['Lab tests', 'Lab categories', 'Medicines', 'Diagnosis', 'Complaints', 'Ward Setup']
        }
      }
    }
  };

  if (role === 'admin') {
    menuItems['admin'] = {
        title: 'Administration',
        items: {
          'user-management': {
            title: 'User Management',
            subItems: ['Users']
          }
        }
      };
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="lg:flex lg:min-h-screen">
        <Sidebar menuItems={menuItems} activeSection={activeSection} setActiveSection={setActiveSection} />
        <MainPage>
          <div className="relative">
            <Outlet />
            
            {/* Call Simulation Trigger (Hidden in bottom right) */}
            <button 
              onClick={simulateCall}
              className="fixed bottom-4 right-4 w-10 h-10 bg-slate-900/10 hover:bg-indigo-600 hover:text-white text-slate-400 rounded-full flex items-center justify-center transition-all opacity-20 hover:opacity-100 z-[99]"
              title="Simulator: Incoming Call"
            >
              <div className="w-2 h-2 rounded-full bg-current animate-ping" />
            </button>
          </div>
        </MainPage>
      </div>

      {/* Global Inbound Call Notification */}
      {incomingCall && (
        <IncomingCallToast 
           callerName={incomingCall.callerName}
           onAccept={() => {
              setActiveCall({ 
                  id: incomingCall.id, 
                  patientName: incomingCall.callerName,
                  patientId: incomingCall.patientId,
                  doctorId: incomingCall.doctorId
              });
              setIncomingCall(null);
           }}
           onReject={() => setIncomingCall(null)}
        />
      )}

      {/* Global Video Call Overlay */}
      {activeCall && (
        <VideoCallModal 
           isOpen={true} 
           onClose={() => setActiveCall(null)} 
           appointment={activeCall} 
        />
      )}
    </div>
  );
};

export default Dashboard;